import { useState, useEffect } from "react";
import { useSearch } from "wouter";

/**
 * All available status types for filtering
 */
export const ALL_STATUSES = [
  "normal",
  "offline",
  "danger",
  "alerted",
  "nodata",
  "standby",
];

/**
 * Ranges for the Horsepower filter
 */
export const HORSEPOWER_RANGES = [
  { label: "0-100", rangeLow: 0, rangeHigh: 100 },
  { label: "100-300", rangeLow: 100, rangeHigh: 300 },
  { label: "300-600", rangeLow: 300, rangeHigh: 600 },
  { label: "600-1000", rangeLow: 600, rangeHigh: 1000 },
  { label: "1000-1500", rangeLow: 1000, rangeHigh: 1500 },
  { label: "1500+", rangeLow: 1500, rangeHigh: 999999 },
];

/**
 * @typedef {Object} Filters
 * @property {string[]} vendor - Selected vendor filter values
 * @property {string[]} location - Selected location filter values
 * @property {string[]} telemetrySource - Selected telemetry source filter values
 * @property {boolean|null} optimizationEnabled - true | false | null (null = all)
 * @property {string[]} status - Selected status filter values (multiple allowed)
 * @property {string[]} assetNumbers - Selected asset numbers for filtering
 * @property {string[]} foreman - Selected foreman for filtering
 * @property {string[]} customer - Selected customer for filtering
 * @property {string[]} engineModel - Selected engine models for filtering
 * @property {string[]} compressorModel - Selected compressor models for filtering
 * @property {string[]} horsepowerRange - Horsepower in range of values
 */

const FILTERS_STORAGE_KEY = "fleet-filters";

// Helper function to check if all statuses are active
export const isAllActive = (statuses) =>
  statuses.length === ALL_STATUSES.length
  && ALL_STATUSES.every((s) => statuses.includes(s));

// Default filter values
const DEFAULT_FILTERS = {
  vendor: [],
  location: [],
  telemetrySource: [],
  optimizationEnabled: null, // null = all, true = only Enalysis/Enbase, false = only non-optimization
  status: ALL_STATUSES, // All statuses active by default
  assetNumbers: null, // null means all assets, [] means none, [values] means only those
  foreman: [],
  customer: [],
  engineModel: [],
  compressorModel: [],
  horsepowerRange: [],
};

const loadFiltersFromStorage = () => {
  try {
    const filterString = localStorage.getItem(FILTERS_STORAGE_KEY);
    if (filterString) {
      const parsedFilters = JSON.parse(filterString);
      // Validate that the stored data has the expected structure
      if (parsedFilters && typeof parsedFilters === "object") {
        return {
          vendor: Array.isArray(parsedFilters.vendor)
            ? parsedFilters.vendor
            : DEFAULT_FILTERS.vendor,
          location: Array.isArray(parsedFilters.location)
            ? parsedFilters.location
            : DEFAULT_FILTERS.location,
          telemetrySource: Array.isArray(parsedFilters.telemetrySource)
            ? parsedFilters.telemetrySource
            : DEFAULT_FILTERS.telemetrySource,
          optimizationEnabled: (() => {
            const v = parsedFilters.optimizationEnabled;
            if (v === true || v === false) return v;
            if (v === "Yes") return true;
            if (v === "No") return false;
            return DEFAULT_FILTERS.optimizationEnabled;
          })(),
          status: Array.isArray(parsedFilters.status)
            ? parsedFilters.status
            : DEFAULT_FILTERS.status,
          assetNumbers: Array.isArray(parsedFilters.assetNumbers)
            ? parsedFilters.assetNumbers
            : DEFAULT_FILTERS.assetNumbers,
          foreman: Array.isArray(parsedFilters.foreman)
            ? parsedFilters.foreman
            : DEFAULT_FILTERS.foreman,
          customer: Array.isArray(parsedFilters.customer)
            ? parsedFilters.customer
            : DEFAULT_FILTERS.customer,
          engineModel: Array.isArray(parsedFilters.engineModel)
            ? parsedFilters.engineModel
            : DEFAULT_FILTERS.engineModel,
          compressorModel: Array.isArray(parsedFilters.compressorModel)
            ? parsedFilters.compressorModel
            : DEFAULT_FILTERS.compressorModel,
          horsepowerRange: Array.isArray(parsedFilters.horsepowerRange)
            ? parsedFilters.horsepowerRange
            : DEFAULT_FILTERS.horsepowerRange,
        };
      }
    }
  } catch (error) {
    console.warn("Failed to load filters from localStorage:", error);
  }
  return DEFAULT_FILTERS;
};

const saveFiltersToStorage = (filters) => {
  try {
    localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
  } catch (error) {
    console.warn("Failed to save filters to localStorage:", error);
  }
};

/**
 * Custom hook for managing filter state and handlers
 * @returns {Object} Filter state and handler functions
 */
export function useFilters() {
  /** @type {[Filters, Function]} */
  const [filters, setFilters] = useState(loadFiltersFromStorage);
  const locationSearch = useSearch();

  useEffect(() => {
    saveFiltersToStorage(filters);
  }, [filters]);

  // Sync optimizationEnabled filter from URL when navigating back (e.g. from asset page)
  useEffect(() => {
    const params = new URLSearchParams(
      locationSearch?.startsWith("?")
        ? locationSearch
        : `?${locationSearch || ""}`
    );
    const optimizationEnabled = params.get("optimizationEnabled");
    if (optimizationEnabled === "Yes" || optimizationEnabled === "No") {
      setFilters((prev) => ({
        ...prev,
        optimizationEnabled: optimizationEnabled === "Yes",
      }));
    }
  }, [locationSearch]);

  /**
   * Handles filter changes for any filter key
   * @param {string} key - The filter key to update
   * @param {string|string[]} value - The new filter value(s)
   */
  const handleFilterChange = (key, value) => {
    setFilters(
      /** @param {Filters} prev */ (prev) => ({ ...prev, [key]: value })
    );
  };

  /**
   * Handles status filter changes from status cards
   * @param {string} status - The status to toggle or "all"
   */
  const handleStatusClick = (status) => {
    setFilters(
      /** @param {Filters} prev */ (prev) => {
        if (status === "all") {
          // Total Fleet card clicked - toggle between all statuses and no statuses
          if (isAllActive(prev.status)) {
            // Currently all active, so turn all off
            return { ...prev, status: [] };
          } else {
            // Currently some or none active, so turn all on
            return { ...prev, status: ALL_STATUSES };
          }
        }

        const currentStatuses = prev.status;

        // Check if all individual status cards are currently selected
        const allIndividualSelected = isAllActive(currentStatuses);

        if (allIndividualSelected) {
          // If all individual cards are selected, clicking any individual card
          // should deselect all and only show that specific status
          return {
            ...prev,
            status: [status],
          };
        }

        if (currentStatuses.includes(status)) {
          // Remove status from filters (make it inactive)
          const newStatuses = currentStatuses.filter((s) => s !== status);

          // If no individual statuses are left, show all (Total Fleet active)
          if (newStatuses.length === 0) {
            return { ...prev, status: ALL_STATUSES };
          }

          return {
            ...prev,
            status: newStatuses,
          };
        } else {
          // Add status to filters (make it active)
          const newStatuses = [...currentStatuses, status];

          // Check if all individual statuses are now selected
          const allNowSelected = isAllActive(newStatuses);

          if (allNowSelected) {
            // All individual cards are selected, so show all (Total Fleet active)
            return { ...prev, status: ALL_STATUSES };
          }

          return {
            ...prev,
            status: newStatuses,
          };
        }
      }
    );
  };

  /**
   * Resets all filters to their default values
   */
  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    // Clear from localStorage as well
    try {
      localStorage.removeItem(FILTERS_STORAGE_KEY);
    } catch (error) {
      console.warn("Failed to clear filters from localStorage:", error);
    }
  };

  return {
    filters,
    handleFilterChange,
    handleStatusClick,
    clearFilters,
  };
}
